import React from "react";
import {
	FormType,
	NumberField,
	NumberFieldInputProps,
	ScalarFieldValue,
	SelectFieldInputProps,
	SelectInput,
	useFormState,
} from "@hex-insights/forms";
import {
	Contract,
	defaultCurrency,
	InvoiceStatus,
	Student,
	usePaymentLineItemInvoiceSelectionInformationQuery,
} from "../../../Utilities";
import { PaymentLineItemFormValues } from "../../FormUtilities";
import { InvoiceUtilities } from "../../Utilities";
import { NumberWithCurrencyInput } from "../_General";
import { ContractSelectField } from "../Contract";
import { InvoiceSelectField } from "../Invoice";
import { PaymentSelectField } from "../Payment";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";

/**
 * Generic props for fields of the PaymentLineItem model.
 */
type FieldProps<K extends keyof PaymentLineItemFormValues.Base = keyof PaymentLineItemFormValues.Base> = BaseFieldProps<
	Pick<PaymentLineItemFormValues.Base, K>
>;

/**
 * Field component for the `displayOrder` field of the PaymentLineItem model.
 */
export function DisplayOrder({ formState, ...props }: FieldProps<"displayOrder">) {
	return (
		<NumberField
			formState={formState}
			name="displayOrder"
			optional={FormType.isCreate(formState.formType)}
			validationUnit={1}
			{...props}
		/>
	);
}

/**
 * Field component for the `amount` field of the PaymentLineItem model.
 */
export function Amount({ formState, ...props }: FieldProps<"amount">) {
	const Input = React.useCallback(
		(props: NumberFieldInputProps) => <NumberWithCurrencyInput {...props} currency={defaultCurrency} />,
		[],
	);

	return (
		<NumberField formState={formState} name="amount" validationUnit={1} min={0} unit={100} Input={Input} {...props} />
	);
}

/**
 * Field component for the `localAmount` field of the PaymentLineItem model.
 */
export function LocalAmount({
	formState,
	localCurrency,
	...props
}: FieldProps<"localAmount"> & { localCurrency: string }) {
	const Input = React.useCallback(
		(props: NumberFieldInputProps) => <NumberWithCurrencyInput {...props} currency={localCurrency} />,
		[localCurrency],
	);

	return (
		<NumberField
			formState={formState}
			name="localAmount"
			validationUnit={1}
			min={0}
			unit={100}
			Input={Input}
			{...props}
		/>
	);
}

export namespace Invoice {
	type HelperFormValues = {
		studentID: Student["id"] | null;
		contractID: Contract["id"] | null;
	};

	const initialHelperFormValues: HelperFormValues = {
		studentID: null,
		contractID: null,
	};

	export type SelectProps = FieldProps<"invoiceID" | "amount"> &
		Omit<InvoiceSelectField.SelectProps<PaymentLineItemFormValues.Base>, "formState" | "name" | "optional"> & {
			studentID?: Student["id"];
			contractID?: Contract["id"];
		};

	/**
	 * Field component for the `invoice` edge of the PaymentLineItem model.
	 */
	export function Select({
		formState,
		studentID: initialStudentID,
		contractID: initialContractID,
		...props
	}: SelectProps) {
		const {
			formSetFunctions: { amount: setAmount },
			formClearFunctions: { invoiceID: clearInvoiceID },
		} = formState;

		const helperFormState = useFormState({
			initialFormValues: {
				...initialHelperFormValues,
				studentID: initialStudentID ?? null,
				contractID: initialContractID ?? null,
			},
		});
		const {
			formValues: { studentID, contractID },
			formSetFunctions: { studentID: setStudentID, contractID: setContractID },
			formClearFunctions: { contractID: clearContractID },
		} = helperFormState;

		const { data } = usePaymentLineItemInvoiceSelectionInformationQuery({
			variables: {
				id: formState.formValues.invoiceID ?? "",
			},
			skip: !formState.formValues.invoiceID,
		});
		const invoice = data?.invoice;

		React.useEffect(() => {
			if (!invoice) {
				return;
			}
			const contract = invoice.contractInstallment.contract;
			setStudentID(contract.student.id);
			setContractID(contract.id);
			setAmount(InvoiceUtilities.calculateOutstandingBalance(invoice));
		}, [invoice, setStudentID, setContractID, setAmount]);

		const contractQueryOptions = React.useMemo<ContractSelectField.SelectProps<any>["queryOptions"]>(() => {
			if (!studentID) {
				// to avoid loading too much data
				return {
					variables: {
						filters: {
							idEQ: "0",
						},
					},
				};
			}

			return {
				variables: {
					filters: {
						studentIDEQ: studentID,
					},
				},
			};
		}, [studentID]);

		const invoiceQueryOptions = React.useMemo<InvoiceSelectField.SelectProps<any>["queryOptions"]>(() => {
			if (!contractID) {
				return {
					variables: {
						filters: {
							idEQ: "0",
						},
					},
				};
			}

			return {
				variables: {
					filters: {
						statusEQ: InvoiceStatus.Issued,
						contractInstallment: [
							{
								contractIDEQ: contractID,
							},
						],
					},
				},
			};
		}, [contractID]);

		const StudentInput = React.useCallback(
			<T extends ScalarFieldValue>(props: SelectFieldInputProps<T>) => {
				const setValueSideEffect = () => {
					clearContractID();
					clearInvoiceID();
				};

				return <CustomInput {...props} setValueSideEffect={setValueSideEffect} />;
			},
			[clearContractID, clearInvoiceID],
		);

		const ContractInput = React.useCallback(
			<T extends ScalarFieldValue>(props: SelectFieldInputProps<T>) => {
				const setValueSideEffect = () => {
					clearInvoiceID();
				};

				return <CustomInput {...props} setValueSideEffect={setValueSideEffect} />;
			},
			[clearInvoiceID],
		);

		return (
			<React.Fragment>
				<StudentSelectField.Select
					formState={helperFormState}
					name="studentID"
					noHeader={props.noHeader}
					disabled={props.disabled}
					Input={StudentInput}
				/>
				<ContractSelectField.Select
					formState={helperFormState}
					name="contractID"
					queryOptions={contractQueryOptions}
					noHeader={props.noHeader}
					disabled={props.disabled || !studentID}
					Input={ContractInput}
				/>
				<InvoiceSelectField.Select
					formState={formState}
					name="invoiceID"
					{...props}
					queryOptions={invoiceQueryOptions}
					disabled={props.disabled || !contractID}
				/>
			</React.Fragment>
		);
	}

	function CustomInput<T extends ScalarFieldValue>({
		setValue,
		setValueSideEffect,
		...props
	}: SelectFieldInputProps<T> & { setValueSideEffect?: (value: T) => void }) {
		const wrappedSetValue = React.useCallback(
			(value: T) => {
				setValue(value);
				setValueSideEffect?.(value);
			},
			[setValue, setValueSideEffect],
		);

		return <SelectInput setValue={wrappedSetValue} {...props} />;
	}
}

export namespace Payment {
	export type SelectProps = FieldProps<"paymentID"> &
		Omit<PaymentSelectField.SelectProps<PaymentLineItemFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `payment` edge of the PaymentLineItem model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PaymentSelectField.Select formState={formState} name="paymentID" {...props} />;
	}
}
